import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * LINKS THAT OPEN THE APP.
 *
 * Two mechanisms arrive at the same handler:
 *
 *   1. A universal link — a real https URL on the app's own domain. iOS matches it against
 *      the association file served from that domain (public/.well-known/apple-app-site-
 *      association, deployed with the web build) and opens the app instead of Safari.
 *   2. A custom scheme — `<scheme>://path`. Needed because a universal link cannot be
 *      tested before the domain is live, and because it is the return path from a social
 *      login that hands control back to the app.
 *
 * Both are stripped down to a path here so nothing downstream has to care which one it was.
 */
const supported = Capacitor.isNativePlatform();

export function toAppPath(url: string, webOrigin: string): string | null {
  try {
    const parsed = new URL(url);
    // A custom scheme has the first path segment in `host`: app://reset/abc → host "reset".
    if (!parsed.protocol.startsWith("http")) {
      const host = parsed.host ? `/${parsed.host}` : "";
      return `${host}${parsed.pathname}${parsed.search}` || "/";
    }
    // A universal link only counts if it is OUR domain — anything else belongs to Safari.
    if (`${parsed.protocol}//${parsed.host}` !== webOrigin) return null;
    return `${parsed.pathname}${parsed.search}` || "/";
  } catch {
    return null;
  }
}

export function onAppUrlOpen(handler: (url: string) => void): () => void {
  if (!supported) return () => {};
  const sub = App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => handler(event.url));
  return () => void sub.then((h) => h.remove());
}

/**
 * The URL that STARTED the app, as opposed to one delivered to a running app. iOS gives
 * these through different paths and forgetting the cold-start one means links work
 * perfectly in testing (app already open) and never for a real user.
 */
export async function getLaunchUrl(): Promise<string | null> {
  if (!supported) return null;
  const launch = await App.getLaunchUrl().catch(() => null);
  return launch?.url ?? null;
}
